import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Calculator, StampIcon } from 'lucide-react';
import { company, navLinks, waLink } from '../mock';
import { useEnquiry } from '../context/EnquiryContext';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { openEnquiry } = useEnquiry();
  const prefix = location.pathname === '/' ? '' : '/';
  const link = (href) => (href.startsWith('#') ? `${prefix}${href}` : href);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-[0_6px_24px_rgba(11,37,69,0.08)] py-2' : 'bg-white py-3'
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between">
        <Link to="/" className="flex items-center">
          <img src={company.logo} alt={company.name} className={`transition-all duration-300 ${scrolled ? 'h-11' : 'h-14'} w-auto`} />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map((l) => (
            <a
              key={l.label}
              href={link(l.href)}
              className="relative text-[15px] font-medium text-[#0B2545] hover:text-[#EA5019] transition-colors after:absolute after:left-0 after:-bottom-1.5 after:h-[2px] after:w-0 after:bg-[#EA5019] after:transition-all hover:after:w-full"
            >
              {l.label}
            </a>
          ))}
          <Link to="/visas" data-testid="nav-visas" className="relative text-[15px] font-medium text-[#0B2545] hover:text-[#EA5019] transition-colors flex items-center gap-1">
            <StampIcon size={16} className="text-[#EA5019]" /> Visas
          </Link>
          <Link to="/estimator" className="relative text-[15px] font-medium text-[#0B2545] hover:text-[#EA5019] transition-colors flex items-center gap-1">
            <Calculator size={16} className="text-[#EA5019]" /> Estimator
          </Link>
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a href={`tel:${company.phone.replace(/\\s/g, '')}`} className="flex items-center gap-2 text-[#0B2545] font-semibold text-sm">
            <span className="grid place-items-center h-9 w-9 rounded-full bg-[#0B2545]/5">
              <Phone size={16} className="text-[#EA5019]" />
            </span>
            {company.phone}
          </a>
          <button
            onClick={() => openEnquiry('Flight')}
            data-testid="header-enquiry-btn"
            className="px-5 py-2.5 rounded-full bg-[#EA5019] text-white text-sm font-semibold hover:bg-[#cf4413] transition-colors shadow-[0_8px_20px_rgba(234,80,25,0.35)] hover:-translate-y-0.5 duration-200"
          >
            Get a Free Quote
          </button>
        </div>

        <button className="lg:hidden text-[#0B2545]" onClick={() => setOpen(true)} aria-label="Open menu">
          <Menu size={28} />
        </button>
      </div>

      {/* Mobile drawer */}
      <div className={`fixed inset-0 z-50 lg:hidden transition-opacity duration-300 ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
        <div className="absolute inset-0 bg-[#0B2545]/50" onClick={() => setOpen(false)} />
        <div className={`absolute right-0 top-0 h-full w-[78%] max-w-xs bg-white shadow-2xl p-6 flex flex-col transition-transform duration-300 ${open ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="flex items-center justify-between mb-8">
            <img src={company.logo} alt={company.name} className="h-10 w-auto" />
            <button onClick={() => setOpen(false)} aria-label="Close menu"><X size={26} className="text-[#0B2545]" /></button>
          </div>
          <nav className="flex flex-col gap-1">
            {navLinks.map((l) => (
              <a key={l.label} href={link(l.href)} onClick={() => setOpen(false)} className="py-3 border-b border-slate-100 text-[#0B2545] font-medium hover:text-[#EA5019]">
                {l.label}
              </a>
            ))}
            <Link to="/visas" onClick={() => setOpen(false)} className="py-3 border-b border-slate-100 text-[#0B2545] font-medium hover:text-[#EA5019] flex items-center gap-2">
              <StampIcon size={16} className="text-[#EA5019]" /> Visas
            </Link>
            <Link to="/estimator" onClick={() => setOpen(false)} className="py-3 border-b border-slate-100 text-[#0B2545] font-medium hover:text-[#EA5019] flex items-center gap-2">
              <Calculator size={16} className="text-[#EA5019]" /> Trip Estimator
            </Link>
          </nav>
          <button onClick={() => { setOpen(false); openEnquiry('Flight'); }} className="mt-6 w-full text-center px-5 py-3 rounded-full bg-[#EA5019] text-white font-semibold">
            Get a Free Quote
          </button>
          <a href={`tel:${company.phone.replace(/\\s/g, '')}`} className="mt-3 text-center text-[#0B2545] font-semibold">{company.phone}</a>
        </div>
      </div>
    </header>
  );
}
